import { HrmCodeAttendDto } from 'src/dtos/hrmCodeAttend/hrmCodeAttendDto';
import {
    HRM2010_R01DetailDto,
    HRM2010_R01HeaderDto,
    HRM2010_R01OutPair,
} from './Dto_HRM2010_R01';

/** 00:00부터 23:59까지의 HH:MM 형식을 검사한다. */
export const HRM2010_R01_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** 입력된 시각이 HH:MM 형식이면 true이다. */
export const validateHRM2010_R01Time = (value: string): boolean => HRM2010_R01_TIME_PATTERN.test(value);

/**
 * 그리드에 입력한 시각을 HH:MM으로 정리한다. 0930 → 09:30, 2130 → 21:30, 9:00 → 09:00을 허용한다.
 * HRM3050_R01의 formatHRM3050_R01Time과 같은 규칙이다.
 * @param value 입력값
 * @returns 정리한 HH:MM 문자열. 숫자 형태로 바꿀 수 없으면 앞뒤 공백만 지운 원래 값을 반환해 형식 검사에서 걸러지게 한다.
 */
export const formatHRM2010_R01Time = (value: unknown): string => {
    // ??: 값이 null·undefined이면 빈 문자열로 다룬다.
    const text = String(value ?? '').trim();
    // 정규식 /^(\d{1,2}):?(\d{2})$/: 콜론이 있든 없든 시 1~2자리 + 분 2자리를 찾는다.
    const matched = text.match(/^(\d{1,2}):?(\d{2})$/);
    if (!matched) return text;
    // 구조 분해: 정규식 결과에서 시·분 부분만 꺼낸다.
    const [, hour, minute] = matched;
    return `${hour.padStart(2, '0')}:${minute}`;
};

/** KPI 한 칩의 표시값과 강조 색이다. */
export interface HRM2010_R01Kpi {
    key: string; // 칩 식별값
    label: string; // 칩 제목
    value: string; // 화면 표시 문구
    tone: 'normal' | 'red' | 'orange'; // 강조 색
}

/** 서버 시간 값을 지정 자릿수로 표시한다. */
export const formatHRM2010_R01Hours = (value: number | null | undefined, digits: number): string =>
    (value ?? 0).toFixed(digits);

/**
 * 근태가 없는 재직자로 서버가 만든 미입력 행을 아직 편집하지 않았으면 true이다.
 * 이 행은 사원·부서·근무조·출퇴근만 보여 주고, 편집하면 저장 시 신규 등록된다.
 * @param row 확인할 위 그리드 행
 * @returns 저장되지 않았고 편집도 하지 않은 미입력 행이면 true
 */
export const isHRM2010_R01UnsavedRow = (row?: HRM2010_R01HeaderDto | null): boolean =>
    // ?.: 합계·빈 행처럼 데이터가 없으면 undefined가 되어 false로 처리한다.
    !!row?.isAttendMissing && !row.isModified;

/** 삭제 표시를 제외한 저장된 행으로 화면 KPI 칩을 만들고, 미입력 행은 인원만 따로 센다. */
export const buildHRM2010_R01KpiList = (
    list: HRM2010_R01HeaderDto[],
    deletedList: string[],
): HRM2010_R01Kpi[] => {
    const activeRows = list.filter(row => !deletedList.includes(String(row.valueIndex)));
    /** 저장되지 않은 미입력 행(isAttendMissing)이다. KPI 집계에서 빼고 인원만 표시한다. */
    const missingCount = activeRows.filter(row => row.isAttendMissing).length;
    const rows = activeRows.filter(row => !row.isAttendMissing);
    const groupCount = (group: string): number => rows.filter(row => row.attendGroup === group).length;
    const day = rows.filter(row => !row.isNightShift).length;
    const night = rows.filter(row => row.isNightShift).length;
    const late = rows.filter(row => row.lateMin > 0);
    const early = rows.filter(row => row.earlyMin > 0);
    const out = rows.filter(row => row.outHours > 0);
    const pub = rows.filter(row => row.pubOutHours > 0);
    const sum = (
        field: 'lateMin' | 'earlyMin' | 'outHours' | 'pubOutHours' | 'overHours',
        values: HRM2010_R01HeaderDto[],
    ): number => values.reduce((total, row) => total + row[field], 0);
    return [
        { key: 'target', label: '대상', value: String(rows.length), tone: 'normal' },
        // 삼항 연산자: 미입력 인원이 있으면 주황색으로 강조한다.
        { key: 'missing', label: '미입력', value: String(missingCount), tone: missingCount ? 'orange' : 'normal' },
        { key: 'shift', label: '주간·야간', value: `${day} · ${night}`, tone: 'normal' },
        { key: 'attend', label: '출근', value: String(groupCount('G1')), tone: 'normal' },
        { key: 'vacation', label: '휴가', value: String(groupCount('G2')), tone: 'normal' },
        {
            key: 'absence',
            label: '결근',
            value: String(groupCount('G3')),
            tone: groupCount('G3') ? 'red' : 'normal',
        },
        { key: 'business', label: '출장·교육', value: String(groupCount('G4')), tone: 'normal' },
        {
            key: 'late',
            label: '지각',
            value: `${late.length}명 · ${sum('lateMin', late)}분`,
            tone: late.length ? 'orange' : 'normal',
        },
        {
            key: 'early',
            label: '조퇴',
            value: `${early.length}명 · ${sum('earlyMin', early)}분`,
            tone: early.length ? 'orange' : 'normal',
        },
        {
            key: 'out',
            label: '외출',
            value: `${out.length}명 · ${formatHRM2010_R01Hours(sum('outHours', out), 1)}h`,
            tone: out.length ? 'red' : 'normal',
        },
        {
            key: 'pub',
            label: '공용외출',
            value: `${pub.length}명 · ${formatHRM2010_R01Hours(sum('pubOutHours', pub), 1)}h`,
            tone: 'normal',
        },
        {
            key: 'over',
            label: '연장 합계',
            value: `${formatHRM2010_R01Hours(sum('overHours', rows), 1)}h`,
            tone: 'normal',
        },
    ];
};

/** 시각을 근무일자 0시부터의 분으로 바꾸며 형식 오류는 null이다. */
const toMinute = (value: string): number | null => {
    if (!validateHRM2010_R01Time(value)) return null;
    return Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5));
};

/** 야간조의 다음날 시각만 출퇴근 범위에 맞춰 이동한다. */
const adjustMinute = (value: number, start: number | null, end: number | null): number =>
    start !== null && end !== null && value < start && value + 1440 <= end ? value + 1440 : value;

/** 외출 요약 표시를 위해 서버와 같은 순서로 외출·복귀 짝을 만든다. 시간 저장에는 사용하지 않는다. */
export const buildHRM2010_R01OutPairList = (
    detailList: HRM2010_R01DetailDto[],
    startTime: string,
    endTime: string,
    attendCodeList: HrmCodeAttendDto[],
): HRM2010_R01OutPair[] => {
    const start = toMinute(startTime);
    const rawEnd = toMinute(endTime);
    const end = start !== null && rawEnd !== null && rawEnd < start ? rawEnd + 1440 : rawEnd;
    const rows = detailList
        .filter(row => toMinute(row.eventTime) !== null)
        .sort((a, b) => {
            const first = adjustMinute(toMinute(a.eventTime) ?? 0, start, end);
            const second = adjustMinute(toMinute(b.eventTime) ?? 0, start, end);
            return first - second || a.eventSeq - b.eventSeq;
        });
    const pairs: HRM2010_R01OutPair[] = [];
    let opened: HRM2010_R01OutPair | null = null;
    for (const row of rows) {
        const group = attendCodeList.find(code => code.attendCd === row.attendCd)?.attendGroup;
        if (group === 'G5') {
            if (opened) pairs.push({ ...opened, errorMessage: '복귀 없이 다시 외출' });
            opened = {
                attendCd: row.attendCd,
                fromTime: row.eventTime,
                toTime: null,
                errorMessage: null,
            };
        } else if (group === 'G6') {
            if (opened) {
                pairs.push({ ...opened, toTime: row.eventTime });
                opened = null;
            } else {
                pairs.push({
                    attendCd: row.attendCd,
                    fromTime: null,
                    toTime: row.eventTime,
                    errorMessage: '외출 없이 복귀',
                });
            }
        }
    }
    if (opened) pairs.push({ ...opened, errorMessage: '복귀 미등록' });
    return pairs;
};

/** 서버가 계산한 시간만 넣어 외출 요약 문장을 만든다. */
export const formatHRM2010_R01OutSummary = (row: HRM2010_R01HeaderDto): string =>
    `근무 ${formatHRM2010_R01Hours(row.workHours + row.overHours + row.holidayHours, 2)}h = 출퇴근 − 근무제외 휴게 ${formatHRM2010_R01Hours(row.breakHours, 2)}h − 외출 ${formatHRM2010_R01Hours(row.outHours, 2)}h · 외출·지각·조퇴 시간에서는 모든 휴게시간을 뺌`;

/** 저장 요청의 필수값과 형식만 검사하며 짝·범위 검증은 서버에 맡긴다. */
export const validateHRM2010_R01SaveData = (
    rows: HRM2010_R01HeaderDto[],
    events: HRM2010_R01DetailDto[],
    deletedList: string[],
): string | null => {
    const empNos = new Set<string>();
    for (const row of rows.filter(value => !deletedList.includes(String(value.valueIndex)))) {
        if (!row.empNo) return '사원을 선택하세요.';
        const name = row.empNm || row.empNo;
        if (!row.attendCd) return `${name}: 근태를 선택하세요.`;
        if ((row.startTime && !validateHRM2010_R01Time(row.startTime))
            || (row.endTime && !validateHRM2010_R01Time(row.endTime)))
            return `${name}: 출근·퇴근은 HH:MM 형식(00:00~23:59)이어야 합니다.`;
        if (empNos.has(row.empNo)) return `${name}: 같은 사원이 두 번 입력되었습니다.`;
        empNos.add(row.empNo);
    }
    for (const row of events) {
        const name = rows.find(value => value.empNo === row.empNo)?.empNm || row.empNo;
        if (!row.eventTime) return `${name} ${row.workDt}: 외출·복귀 시각을 입력하세요.`;
        if (!validateHRM2010_R01Time(row.eventTime))
            return `${name} ${row.workDt}: 외출·복귀 시각은 HH:MM 형식이어야 합니다.`;
    }
    return null;
};
